import { useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import api, { formatErr } from "../lib/api";

/**
 * GenericBulkImportDialog — reusable xlsx/csv → API import dialog.
 * Used by Leads page (entity=leads) and Customers page (entity=customers).
 *
 * Props:
 *   entity       — "leads" | "customers" (drives endpoint + column map)
 *   onDone(res)  — called when user clicks Done after a successful import
 *   onCancel     — close dialog without import
 *
 * The component handles client-side xlsx parse, auto column-mapping,
 * preview, dedupe-toggle, then POSTs the row array to /api/{entity}/import.
 */

const COL_MAPS = {
  leads: {
    title: ["title", "lead", "lead title", "name", "lodge name", "business"],
    customer_name: ["customer", "customer name"],
    customer_phone: ["phone", "contact number", "tel", "mobile"],
    customer_email: ["email", "email address"],
    phase: ["phase", "stage"],
    industry: ["industry", "type"],
    province_area: ["province", "area", "prov. area"],
    estate_reserve: ["estate", "reserve", "estate / reserve"],
    star_rating: ["star rating", "stars"],
    price_per_night: ["price per night", "rate"],
    lead_rating: ["lead rating", "rating"],
    lead_source: ["source", "lead source", "entry method"],
    current_provider: ["current provider", "provider", "competitor"],
    value: ["value", "deal value", "amount"],
    notes: ["notes", "comments"],
  },
  customers: {
    name: ["name", "lodge name", "business", "client", "customer"],
    customer_type: ["customer type", "type"],
    contact_name: ["contact person", "contact name", "contact"],
    contact_position: ["position", "title", "role"],
    phone: ["phone", "contact number", "tel", "mobile"],
    email: ["email", "email address"],
    whatsapp: ["whatsapp"],
    industry: ["industry"],
    province_area: ["province", "area", "prov. area"],
    estate_reserve: ["estate", "reserve", "estate / reserve"],
    physical_address: ["physical address", "address"],
    billing_address: ["billing address"],
    star_rating: ["star rating", "stars"],
    price_per_night: ["price per night", "rate"],
    notes: ["notes"],
  },
};

const ENDPOINT = { leads: "/leads/import", customers: "/customers/import" };

function findColumnIndex(headers, candidates) {
  const norm = headers.map((h) => String(h || "").trim().toLowerCase());
  for (const c of candidates) {
    const i = norm.indexOf(c);
    if (i >= 0) return i;
  }
  return -1;
}

export default function GenericBulkImportDialog({ entity = "leads", onDone, onCancel }) {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [filename, setFilename] = useState("");

  const colMap = COL_MAPS[entity] || COL_MAPS.leads;
  const requiredKey = entity === "leads" ? "title" : "name";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    setErr("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!aoa.length) throw new Error("Empty spreadsheet.");
      const hdrs = (aoa[0] || []).map((h) => String(h || "").trim());
      const data = aoa.slice(1).filter((r) => Array.isArray(r) && r.some((c) => c !== "" && c != null));
      const map = {};
      Object.entries(colMap).forEach(([key, candidates]) => {
        map[key] = findColumnIndex(hdrs, candidates);
      });
      setHeaders(hdrs);
      setRows(data);
      setMapping(map);
    } catch (e2) {
      setErr(e2?.message || "Could not parse file");
    }
  };

  const buildPayload = () => rows.map((r) => {
    const row = {};
    Object.entries(mapping).forEach(([key, idx]) => {
      if (idx >= 0) {
        let v = r[idx];
        if (v instanceof Date) v = v.toISOString().slice(0, 10);
        if (v != null && v !== "") row[key] = String(v).trim();
      }
    });
    return row;
  });

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { rows: buildPayload(), skip_duplicates: skipDuplicates };
      if (entity === "leads") payload.auto_link_customer = true;
      const { data } = await api.post(ENDPOINT[entity], payload);
      setResult(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Import failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`${entity}-import-dialog`}>
      {!rows.length && !result && (
        <label className="block hv-card p-6 text-center cursor-pointer hover:border-[#1FB6C1]/50 transition border-dashed">
          <FileSpreadsheet className="w-8 h-8 mx-auto text-[#1FB6C1] mb-2" />
          <div className="text-sm font-bold mb-1">Pick an .xlsx or .csv file</div>
          <div className="text-xs text-[#a0aabf]">Columns auto-detected (name, phone, industry, etc.) — preview before import.</div>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid={`${entity}-import-file`} />
        </label>
      )}

      {err && (
        <div className="border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs p-2 rounded flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {!!rows.length && !result && (
        <>
          <div className="text-xs text-[#a0aabf] flex items-center justify-between">
            <span><span className="text-[#1FB6C1] font-bold">{filename}</span> — {rows.length} rows detected</span>
            <button type="button" onClick={() => { setRows([]); setHeaders([]); setMapping({}); setFilename(""); }} className="text-[#647087] hover:text-white">change file</button>
          </div>

          <div className="hv-card p-3" data-testid={`${entity}-import-mapping`}>
            <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-2">Column mapping</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {Object.keys(colMap).map((key) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className="w-32 text-[#a0aabf] font-mono shrink-0">{key}{key === requiredKey ? "*" : ""}</span>
                  <select
                    value={mapping[key] ?? -1}
                    onChange={(e) => setMapping({ ...mapping, [key]: Number(e.target.value) })}
                    className="hv-input text-xs flex-1"
                    data-testid={`${entity}-import-map-${key}`}
                  >
                    <option value={-1}>— skip —</option>
                    {headers.map((h, i) => <option key={i} value={i}>{h || `(col ${i + 1})`}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)} data-testid={`${entity}-import-dedupe`} />
            Skip duplicates (matches phone or email)
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCancel} className="hv-btn hv-btn-ghost">Cancel</button>
            <button
              onClick={submit}
              disabled={busy || (mapping[requiredKey] ?? -1) < 0}
              className="hv-btn hv-btn-primary"
              data-testid={`${entity}-import-submit`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Import {rows.length} rows
            </button>
          </div>
        </>
      )}

      {result && (
        <div className="hv-card p-4 space-y-2 border-emerald-500/30 bg-emerald-500/5" data-testid={`${entity}-import-result`}>
          <div className="flex items-center gap-2 text-emerald-300">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-bold">Import complete.</span>
          </div>
          <div className="text-sm">
            <span className="text-emerald-300 font-bold">{result.created}</span> {entity} created.
            {result.skipped_duplicates > 0 && <> · <span className="text-amber-300">{result.skipped_duplicates}</span> skipped (duplicates).</>}
            {result.errors?.length > 0 && <> · <span className="text-rose-300">{result.errors.length}</span> errors.</>}
          </div>
          {result.errors?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-rose-300">Show errors</summary>
              <pre className="text-[10px] bg-black/30 p-2 mt-1 rounded overflow-auto max-h-40">{JSON.stringify(result.errors, null, 2)}</pre>
            </details>
          )}
          <div className="flex justify-end pt-1">
            <button onClick={() => onDone?.(result)} className="hv-btn hv-btn-primary text-xs" data-testid={`${entity}-import-done`}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
